/**
 * Synapsy Widget - Entry Point
 * IIFE que inicializa o widget embedável
 */

import { parseConfig } from "./config.js";
import { createLauncher } from "./launcher.js";
import { createBubble } from "./bubble.js";
import { createIframe } from "./iframe.js";
import { maybePlayEntrance } from "./animations.js";
import { onActivate } from "./a11y.js";
import { WindowMessenger, connect } from "penpal";

const WIDGET_STATE_KEY = "synapsy_widget_state";

/**
 * Salva o estado do widget no sessionStorage
 * @param {string} state - 'open' ou 'closed'
 */
function setWidgetState(state) {
  try {
    window.sessionStorage.setItem(WIDGET_STATE_KEY, state);
  } catch (error) {
    console.warn("[Synapsy Widget] Erro ao salvar estado:", error);
  }
}

/**
 * Recupera o estado salvo do widget
 * @returns {string|null} 'open', 'closed' ou null
 */
function getWidgetState() {
  try {
    return window.sessionStorage.getItem(WIDGET_STATE_KEY);
  } catch (error) {
    console.warn("[Synapsy Widget] Erro ao recuperar estado:", error);
    return null;
  }
}

// Ponto único de verdade da visibilidade do estado fechado: launcher sempre
// visível; o balão deriva de "chat fechado" E de não ter sido dispensado
// (flag dataset.dismissed que o próprio balão marca ao clicar no X). Como o
// balão persiste no DOM, a dispensa dura o carregamento e zera no reload.
function showClosedUI(launcher, bubble) {
  launcher.style.display = "flex";
  bubble.style.display = bubble.dataset.dismissed ? "none" : "flex";
}
function hideClosedUI(launcher, bubble) {
  launcher.style.display = "none";
  bubble.style.display = "none";
}

(function () {
  "use strict";

  /**
   * Inicializa o widget após o DOM estar pronto
   */
  function initWidget() {
    // Encontrar o script tag atual
    const currentScript =
      document.currentScript ||
      document.querySelector('script[src*="widget.js"]');

    if (!currentScript) {
      console.error("[Synapsy Widget] Não foi possível encontrar o script tag");
      return;
    }

    const config = parseConfig(currentScript);

    // Verificar se body existe
    if (!document.body) {
      console.error("[Synapsy Widget] document.body não está disponível");
      return;
    }

    const container = document.createElement("div");
    container.id = "synapsy-widget-container";
    document.body.appendChild(container);

    const iframe = createIframe(config);
    container.appendChild(iframe);

    const launcher = createLauncher(config);
    container.appendChild(launcher);

    const bubble = createBubble(config);
    container.appendChild(bubble);

    // Abre o chat. Funciona mesmo sem Penpal; tenta openPanel se já conectado.
    async function openWidget() {
      if (config.debug) {
        console.log("[Synapsy Widget] Abrindo painel...");
      }
      hideClosedUI(launcher, bubble);
      iframe.style.display = "block";
      setWidgetState("open");

      const conn = window.__synapsyConnection;
      if (conn?.child) {
        try {
          await conn.child.openPanel();
        } catch (e) {
          if (config.debug) {
            console.warn("[Synapsy Widget] openPanel falhou:", e);
          }
        }
      }
    }

    // Restaurar estado salvo ANTES de conectar Penpal
    const savedState = getWidgetState();
    if (savedState === "open") {
      iframe.style.display = "block";
      hideClosedUI(launcher, bubble);
    } else {
      // Mostrar UI fechada IMEDIATAMENTE - não esperar Penpal
      showClosedUI(launcher, bubble);
      // Animação de surgimento (deslizar de baixo), 1x por sessão
      maybePlayEntrance(launcher, bubble, config);
    }

    // Clicar OU teclar (Enter/Espaço) no launcher ou no balão abre o chat
    onActivate(launcher, openWidget);
    onActivate(bubble, openWidget);

    // Aguardar iframe carregar antes de estabelecer conexão Penpal
    iframe.addEventListener("load", () => {
      if (config.debug) {
        console.log(
          "[Synapsy Widget] Iframe carregado, estabelecendo conexão Penpal...",
        );
      }
      setupPenpalConnection(iframe, launcher, bubble, config);
    });

    console.log("[Synapsy Widget] Inicializado", config);
  }

  // Aguardar DOM estar pronto
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWidget);
  } else {
    // DOM já está pronto
    initWidget();
  }
})();

/**
 * Configura conexão Penpal entre parent (widget) e child (iframe)
 * @param {HTMLIFrameElement} iframe - Elemento iframe
 * @param {HTMLElement} launcher - Elemento launcher
 * @param {HTMLElement} bubble - Elemento do balão
 * @param {Object} config - Configuração do widget
 */
async function setupPenpalConnection(iframe, launcher, bubble, config) {
  try {
    if (config.debug) {
      console.log("[Synapsy Widget] Estabelecendo conexão Penpal...");
    }

    const messenger = new WindowMessenger({
      remoteWindow: iframe.contentWindow,
      allowedOrigins: config.allowedOrigins,
    });

    const connection = connect({
      messenger,
      methods: {
        /**
         * Ajusta altura do iframe
         * @param {number} height - Nova altura em pixels
         */
        onResize(height) {
          if (!Number.isFinite(height) || height <= 0) {
            if (config.debug) {
              console.warn("[Synapsy Widget] Resize ignorado: valor inválido:", height);
            }
            return;
          }
          if (config.debug) {
            console.log("[Synapsy Widget] Resize solicitado:", height);
          }
          iframe.style.height = `${height}px`;
        },

        /**
         * Oculta iframe e mostra launcher
         */
        onClose() {
          if (config.debug) {
            console.log("[Synapsy Widget] Fechar solicitado");
          }
          iframe.style.display = "none";
          showClosedUI(launcher, bubble);
          setWidgetState("closed");
        },
      },
    });

    const child = await connection.promise;

    if (config.debug) {
      console.log("[Synapsy Widget] Conexão Penpal estabelecida!");
    }

    await child.ready();

    if (config.debug) {
      console.log("[Synapsy Widget] Iframe ready!");
    }

    // Se estado é open, chamar openPanel para sincronizar o painel interno
    if (getWidgetState() === "open") {
      await child.openPanel();
    }

    // Guardar referência para uso posterior
    window.__synapsyConnection = { child, iframe, launcher, bubble };
  } catch (error) {
    console.error("[Synapsy Widget] Erro ao estabelecer conexão:", error);
    // Launcher já está visível (mostrado imediatamente em initWidget)
    // Nenhuma ação adicional necessária
  }
}
